'use strict';

import moment from 'moment-timezone';
import iban from 'iban';
import parsePhoneNumber from 'libphonenumber-js';

import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import {
    formatBool,
    formatDate,
    formatDouble,
    formatDuration,
    formatInteger,
    formatTime,
    formatTimerule,
    formatTimestamp
} from '~/shared/src/utilities/valueFormaters';
import {convertValueToString} from '~/shared/src/utilities/valueConverters';


export type  RendererValueType = PropertyValueType | 'REFTYPEWITHDIRECTION' | 'REFDIRECTION' | 'STATUS' | 'STATUSID' | 'STATUSNAME';
export type RenderParams = {
    BOOLEAN : {};
    COLOR : {
        filterString : string;
    };
    COORDINATES : {
        editMode : boolean;
    };
    CURRENCY : {
        valueUnit : string;
    };
    DATE : {
        timezone : string;
    };
    DOUBLE : {
        valueUnit : string;
        valuePrecision? : number;
    };
    DURATION : {};
    EMAIL : {
        editMode : boolean;
        filterString : string;
    };
    IBAN : {
        filterString : string;
    };
    IMAGEDATA : {
        onlyOneLine : boolean;
    };
    INTEGER : {
        valueUnit : string;
    };
    JSON : {
        filterString : string;
        onlyOneLine : boolean;
    };
    LINK : {
        editMode : boolean;
        filterString : string;
    };
    PHONE : {
        editMode : boolean;
        filterString : string;
    };
    REFDIRECTION : {
        showAdditionalRawNames : boolean;
    };
    STATUS : {
        schema : Schema;
        showAdditionalRawNames : boolean;
    };
    STATUSID : {
        schema : Schema;
        showAdditionalRawNames : boolean;
    };
    STATUSNAME : {
        schema : Schema;
        showAdditionalRawNames : boolean;
    };
    STRING : {
        filterString : string;
        valueUnit : string;
    };
    TEXT : {
        filterString : string;
    };
    TIME : {
        onlyOneLine : boolean;
        timezone : string;
    };
    TIMERULE : {
        filterString : string;
        language : string;
        timezone : string;
    };
    TIMESTAMP : {
        timezone : string;
    };
};


const nl2br = ( str : string ) : string => {
    return String( str ).replace( /\r?\n/g, '<br/>' );
};


/**
 * Renderer for value.
 *
 * @param {RendererValueType} type
 * @param {any} value
 * @param {object} [params={}]                      Any additionally needed params for getting the filter field.
 *                                                  Like valueType, searchString, filterValue, timezone, schema, showAdditionalRawNames, valueSetId, ...
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string |  undefined}
 */
      // TODO: refactor parameters to type, value, rawMode, params
      // TODO: mapped type for params depending on type, see getValueEditor
export const renderValue = ( type : RendererValueType, value : any, params : any = {}, rawMode : boolean = false ) : string | undefined => {
          let retVal : string | undefined;

          switch ( type ) {
              case 'BOOLEAN':
                  retVal = boolRenderer( value, rawMode );
                  break;

              case 'COLOR':
                  retVal = colorRenderer( value );
                  break;

              case 'COORDINATE':
                  retVal = coordinateRenderer( value );
                  break;

              case 'CURRENCY':
                  retVal = doubleRenderer( value, params.valueUnit, undefined, rawMode );
                  break;

              case 'DATE':
                  retVal = dateRenderer( value, params.timezone, rawMode );
                  break;

              case 'DOUBLE':
                  retVal = doubleRenderer( value, params.valueUnit, params.valuePrecision, rawMode );
                  break;

              case 'DURATION':
                  retVal = durationRenderer( value, rawMode );
                  break;

              case 'EMAIL':
                  retVal = emailRenderer( value, params.editMode, rawMode );
                  break;

              case 'IBAN':
                  retVal = ibanRenderer( value, rawMode );
                  break;

              case 'IMAGEDATA':
                  retVal = imageRenderer( value, rawMode, params.onlyOneLine );
                  break;

              case 'INTEGER':
                  retVal = integerRenderer( value, params.valueUnit, rawMode );
                  break;

              case 'JSON':
                  retVal = jsonRenderer( value, rawMode, params.onlyOneLine );
                  break;

              case 'LINK':
                  retVal = linkRenderer( value, params.editMode, rawMode );
                  break;

              case 'PHONE':
                  retVal = phoneRenderer( value, params.editMode, rawMode );
                  break;

              case 'TEXT':
                  retVal = textRenderer( value );
                  break;

              case 'TIME':
                  retVal = timeRenderer( value, params.timezone, rawMode );
                  break;

              case 'TIMERULE':
                  retVal = timeruleRenderer( value, params.timezone, params.language, rawMode );
                  break;

              case 'TIMESTAMP':
                  retVal = timestampRenderer( value, params.timezone, rawMode );
                  break;

              case 'STRING':
              default:
                  retVal = stringRenderer( value );
                  break;
          }

          return retVal;
      };

/**
 * Renderer for boolean
 *
 * @param {String} value                            The data value to be rendered
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const boolRenderer = ( value : any, rawMode : boolean = false ) : string => {
    let retVal : string;

    if ( !rawMode ) {
        retVal = formatBool( value );
    }
    else {
        retVal = convertValueToString( value, 'BOOLEAN' );
    }

    return retVal;
};

/**
 * Renderer for color
 *
 * @param {string} value                            The data value to be rendered
 *
 * @return {string}
 */
export const colorRenderer = ( value : string ) : string => {
    return value;
};

/**
 * Renderer for coordinate
 *
 * @param {number[]} value                          The data value to be rendered
 *
 * @return {string}
 */
export const coordinateRenderer = ( value : number[] ) : string => {
    return Array.isArray( value ) ? value.join( ', ' ) : String( value );
};

/**
 * Renderer for date
 *
 * @param {String} value                            The data value to be rendered
 * @param {string} [timezone='Europe/Berlin']       The timezone to convert the timestamp to
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const dateRenderer = ( value : string | Date, timezone : string = 'Europe/Berlin', rawMode : boolean = false ) : string => {
    let retVal : string;

    if ( !rawMode ) {
        retVal = formatDate( value );
    }
    else {
        value = typeof value === 'string' ? moment( value ).toDate() : value;
        retVal = convertValueToString( value, 'DATE' );
    }

    return retVal;
};

/**
 * Renderer for double
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} [unit='']                        The unit of the value
 * @param {number} [precision]                      The number of fraction digits to show the value with
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const doubleRenderer = ( value : number | string, unit : string = '', precision? : number, rawMode : boolean = false ) : string => {
    let retVal : string;

    if ( !rawMode ) {
        retVal = formatDouble( value, unit, precision );
    }
    else {
        retVal = convertValueToString( value, 'DOUBLE' );
    }

    return retVal;
};

/**
 * Renderer for double
 *
 * @param {number} value                            The data value to be rendered
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const durationRenderer = ( value : number | string, rawMode : boolean = false ) : string => {
    let retVal : string;

    if ( !rawMode ) {
        retVal = formatDuration( value );
    }
    else {
        retVal = convertValueToString( value, 'DOUBLE' );
    }

    return retVal;
};

/**
 * Renderer for email
 *
 * @param {string} value                            The data value to be rendered
 * @param {boolean} [editMode=false]
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {( string | JSX.Element )[]}
 */
export const emailRenderer = ( value : string, editMode : boolean = false, rawMode : boolean = false ) : string => {
    let retVal : string = value;

    if ( !rawMode && !editMode ) {
        retVal = `<a href="mailto:${value}" target="_blank" style="color: inherit;" class="yn-enabled"><span class="pi pi-envelope"></span></a> <span>${retVal}</span>`;
    }
    else if ( !rawMode && editMode ) {
        retVal = `<span class="pi pi-envelope"></span> ${retVal}`;
    }

    return retVal;
};

/**
 * Renderer for IBAN
 *
 * @param {string} value                            The data value to be rendered
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const ibanRenderer = ( value : string, rawMode : boolean = false ) : string => {
    let retVal : string | JSX.Element | JSX.Element[] = value;

    if ( !rawMode ) {
        const parsedIban                = iban.printFormat( value ) ?? value,
              spacePositions : number[] = [];

        let index     = 0,
            charCount = 0,
            tagOpen   = false;

        do {
            spacePositions.push( parsedIban.indexOf( ' ', spacePositions[ spacePositions.length - 1 ] + 1 ?? 0 ) );
        } while ( spacePositions[ spacePositions.length - 1 ] !== -1 )

        while ( index < retVal.length ) {
            if ( retVal.charAt( index ) === '<' ) {
                tagOpen = true;
            }
            else if ( retVal.charAt( index ) === '>' ) {
                tagOpen = false;
            }
            else if ( !tagOpen ) {
                charCount++;

                if ( spacePositions.includes( charCount - 1 ) ) {
                    retVal = retVal.substring( 0, index ) + '\u00a0' + retVal.substring( index );
                }
            }

            index++;
        }
    }

    return retVal;
};

/**
 * Renderer for image
 *
 * @param {String} value                            The data value to be rendered
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [onlyOneLine=false]             Show value as one line
 *
 * @return {string}
 */
export function imageRenderer( value : string, rawMode : boolean = false, onlyOneLine : boolean = false ) : string {
    let retVal : string = value;

    if ( !rawMode ) {
        if ( !onlyOneLine ) {
            retVal = `<img style="vertical-align: bottom" alt="" src="${value}"/>`;
        }
        else {
            retVal = `<img style="vertical-align: bottom" height="14" alt="" src="${value}"/>`;
        }
    }

    return retVal;
}

/**
 * Renderer for integer
 *
 * @param {number|string} value                     The data value to be rendered
 * @param {string} [unit='']                        The unit of the value
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const integerRenderer = ( value : number | string, unit : string = '', rawMode : boolean = false ) : string => {
    let retVal : string;

    if ( !rawMode ) {
        retVal = formatInteger( value, unit );
    }
    else {
        retVal = convertValueToString( value, 'INTEGER' );
    }

    return retVal;
};

/**
 * Renderer for json
 *
 * @param {String} value                            The data value to be rendered
 * @param {boolean} [rawMode=false]                 Show raw value
 * @param {boolean} [onlyOneLine=false]             Show value as one line
 *
 * @return {string}
 */
export const jsonRenderer = ( value : string, rawMode : boolean = false, onlyOneLine : boolean = false ) : string => {
    let retVal : string = value;

    if ( !rawMode && !onlyOneLine && value ) {
        try {
            value = JSON.stringify( JSON.parse( value ), null, '\u00a0\u00a0\u00a0\u00a0' );
        }
        catch ( e ) {
            console.error( e );
            // preserve given format
            value = value.replace( / /gim, '\u00a0' );
        }

        retVal = nl2br( value );
    }

    return retVal;
};

/**
 * Renderer for link
 *
 * @param {string} value                            The data value to be rendered
 * @param {boolean} [editMode=false]
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const linkRenderer = ( value : string, editMode : boolean = false, rawMode : boolean = false ) : string => {
    let retVal : string = value;

    if ( !rawMode && !editMode ) {
        retVal = `<a href="${value?.match( /:\/\// ) ? value : `https://${value}`}" target="_blank" style="color: inherit;" class="yn-enabled"><span class="pi pi-link"></span></a> <span>${retVal}</span>`;
    }
    else if ( !rawMode && editMode ) {
        retVal = `<span class="pi pi-link"></span> ${retVal}`;
    }

    return retVal;
};

/**
 * Renderer for phone
 *
 * @param {string} value                            The data value to be rendered
 * @param {boolean} [editMode=false]
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const phoneRenderer = ( value : string, editMode : boolean = false, rawMode : boolean = false ) : string => {
    let retVal : string = value;

    if ( !rawMode ) {
        const parsedPhoneNumber         = parsePhoneNumber( value )?.formatInternational() ?? value,
              spacePositions : number[] = [];

        let index     = 0,
            charCount = 0,
            tagOpen   = false;

        do {
            spacePositions.push( parsedPhoneNumber.indexOf( ' ', spacePositions[ spacePositions.length - 1 ] + 1 ?? 0 ) );
        } while ( spacePositions[ spacePositions.length - 1 ] !== -1 )

        while ( index < retVal.length ) {
            if ( retVal.charAt( index ) === '<' ) {
                tagOpen = true;
            }
            else if ( retVal.charAt( index ) === '>' ) {
                tagOpen = false;
            }
            else if ( !tagOpen ) {
                charCount++;

                if ( spacePositions.includes( charCount - 1 ) ) {
                    retVal = retVal.substring( 0, index ) + '\u00a0' + retVal.substring( index );
                }
            }

            index++;
        }
    }

    if ( !rawMode && !editMode ) {
        retVal = `<a href="tel:${value}" target="_blank" style="color: inherit;" class="yn-enabled"><span class="pi pi-phone"></span></a> <span>${retVal}</span>`;
    }
    else if ( !rawMode && editMode ) {
        retVal = `<span class="pi pi-phone"></span> ${retVal}`;
    }

    return retVal;
};

/**
 * renderer for string
 *
 * @param {string} value                            The data value to be rendered
 *
 * @return {string}
 */
export const stringRenderer = ( value : string ) : string => {
    return value;
};

/**
 * renderer for string
 *
 * @param {string} value                            The data value to be rendered
 *
 * @return {string}
 */
export const textRenderer = ( value : string ) : string => {
    return value;
};

/**
 * Renderer for time
 *
 * @param {string|Date} value                       The data value to be rendered
 * @param {string} [timezone='Europe/Berlin']       The timezone to convert the timestamp to
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const timeRenderer = ( value : string | Date, timezone : string = 'Europe/Berlin', rawMode : boolean = false ) : string => {
    let retVal : string;

    if ( !rawMode ) {
        retVal = formatTime( value );
    }
    else {
        value = typeof value === 'string' ? moment( value ).toDate() : value;
        retVal = convertValueToString( value, 'TIME' );
    }

    return retVal;
};

/**
 * Renderer for timerule
 *
 * @param {string} value                            The data value to be rendered
 * @param {string} [timezone='Europe/Berlin']       The timezone to convert the timestamp to
 * @param {string} language                         The language identifier to convert the timestamp with
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const timeruleRenderer = ( value : string, timezone : string = 'Europe/Berlin', language? : string, rawMode : boolean = false ) : string => {
    let retVal : string;

    if ( !rawMode ) {
        retVal = formatTimerule( value, timezone, language );
    }
    else {
        retVal = value.split( /\r?\n/img ).join( '<br/>' );
    }

    return retVal;
};

/**
 * Renderer for timestamp
 *
 * @param {string|Date} value                       The data value to be rendered
 * @param {string} [timezone='Europe/Berlin']       The timezone to convert the timestamp to
 * @param {boolean} [rawMode=false]                 Show raw value
 *
 * @return {string}
 */
export const timestampRenderer = ( value : string | Date, timezone : string = 'Europe/Berlin', rawMode : boolean = false ) : string => {
    let retVal : string;

    if ( !rawMode ) {
        retVal = formatTimestamp( value, timezone );
    }
    else {
        value = typeof value === 'string' ? moment( value ).toDate() : value;
        retVal = convertValueToString( value, 'TIMESTAMP' );
    }

    return retVal;
};
